// Versão: 3.03
// Data..: 2026-10-13
// v3.03: editor do artigo migrado para Tiptap (MarkdownEditor) — reconhece Markdown ao
// digitar/colar; mantém saída em HTML (sem mudança no backend). Troca apenas nesta página.
// v3.02: layout lista + detalhe usa .page-list-detail (colapsa em 1 coluna no mobile).
// Base de conhecimento: lista com filtro (texto + categoria), leitura, criação/edição/
// exclusão (equipe), categoria (árvore) e tags. Usa a mesma árvore de categorias dos chamados.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ArticleVisibility,
  UserRole,
  type KnowledgeArticle,
  type CreateArticleInput,
  type Category,
  type Paginated,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { useDialog } from '../context/DialogContext.js';
import { MarkdownEditor } from '../components/MarkdownEditor.js';
import { PageHeader } from '../components/PageHeader.js';
import { buildCategoryOptions, categoryPath } from '../lib/categories.js';

type Draft = CreateArticleInput & { id?: string };

const BLANK: Draft = { title: '', body: '', visibility: ArticleVisibility.PUBLIC, published: true, categoryId: '', tags: [] };

export function KnowledgePage() {
  const { user } = useAuth();
  const toast = useToast();
  const { confirm } = useDialog();
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selected, setSelected] = useState<KnowledgeArticle | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState(''); // '' = todas; 'none' = sem categoria

  const load = () => {
    api.get<{ items: KnowledgeArticle[] }>('/kb').then((r) => setArticles(r.items)).catch(() => setArticles([])).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    if (isStaff) api.get<Paginated<Category>>('/categories').then((r) => setCategories(r.items)).catch(() => {});
  }, [isStaff]);

  const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories]);

  // Opções do filtro de categoria: categorias efetivamente presentes nos artigos.
  // Rótulo usa o caminho da árvore quando disponível (staff); senão o id como fallback.
  const filterCategoryOptions = useMemo(() => {
    const ids = Array.from(new Set(articles.map((a) => a.categoryId).filter(Boolean) as string[]));
    return ids
      .map((id) => ({ id, label: categoryPath(categories, id) ?? 'Categoria' }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [articles, categories]);

  const hasUncategorized = useMemo(() => articles.some((a) => !a.categoryId), [articles]);

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return articles.filter((a) => {
      if (term && !`${a.title} ${(a.tags ?? []).join(' ')}`.toLowerCase().includes(term)) return false;
      if (catFilter === 'none' && a.categoryId) return false;
      if (catFilter && catFilter !== 'none' && a.categoryId !== catFilter) return false;
      return true;
    });
  }, [articles, filter, catFilter]);

  const startNew = () => { setEditing({ ...BLANK }); setSelected(null); };
  const startEdit = (a: KnowledgeArticle) => {
    setEditing({ id: a.id, title: a.title, body: a.body, visibility: a.visibility, published: a.published, categoryId: a.categoryId ?? '', tags: a.tags ?? [] });
    setSelected(null);
  };

  const save = async (draft: Draft) => {
    const payload: CreateArticleInput = {
      title: draft.title, body: draft.body, visibility: draft.visibility,
      published: draft.published, categoryId: draft.categoryId || undefined, tags: draft.tags,
    };
    if (draft.id) {
      await api.patch(`/kb/${draft.id}`, payload);
      toast.success('Artigo atualizado');
    } else {
      await api.post('/kb', payload);
      toast.success('Artigo criado');
    }
    setEditing(null);
    load();
  };

  const remove = async (a: KnowledgeArticle) => {
    const ok = await confirm({
      title: 'Excluir artigo',
      message: `Excluir o artigo "${a.title}"?`,
      confirmLabel: 'Excluir', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/kb/${a.id}`);
      toast.success('Artigo excluído');
      if (selected?.id === a.id) setSelected(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao excluir');
    }
  };

  return (
    <div>
      <PageHeader
        title="Base de conhecimento"
        description="Artigos de ajuda públicos e internos."
        actions={isStaff ? <button className="btn btn-primary" onClick={startNew}>+ Novo artigo</button> : undefined}
      />

      <div className="page-list-detail">
        {/* Lista */}
        <div className="flex flex-col gap-2">
          <input className="input" placeholder="🔎 Buscar artigo ou tag" value={filter} onChange={(e) => setFilter(e.target.value)} />
          {(filterCategoryOptions.length > 0 || hasUncategorized) && (
            <select className="select" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} aria-label="Filtrar por categoria">
              <option value="">🏷 Todas as categorias</option>
              {filterCategoryOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              {hasUncategorized && <option value="none">— Sem categoria —</option>}
            </select>
          )}
          <div className="card card-flush">
            {loading ? (
              <div className="flex justify-center" style={{ padding: 32 }}><span className="spinner" /></div>
            ) : filtered.length === 0 ? (
              <div className="empty" style={{ padding: 'var(--s5)' }}><div className="empty-icon">📚</div>{articles.length ? 'Nenhum resultado.' : 'Sem artigos.'}</div>
            ) : (
              <div className="flex flex-col">
                {filtered.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => { setSelected(a); setEditing(null); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      padding: '12px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
                      background: selected?.id === a.id ? 'var(--brand-soft)' : 'transparent',
                      color: selected?.id === a.id ? 'var(--brand)' : 'var(--text)',
                      borderBottom: '1px solid var(--border)', font: 'inherit', fontWeight: 550,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                    <span title={a.visibility === ArticleVisibility.PRIVATE ? 'Privado' : 'Público'}>
                      {a.visibility === ArticleVisibility.PRIVATE ? '🔒' : '🌐'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="card">
          {editing ? (
            <ArticleForm
              draft={editing}
              categoryOptions={categoryOptions}
              onCancel={() => setEditing(null)}
              onSave={save}
            />
          ) : selected ? (
            <article>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <span className="badge badge-closed">{selected.visibility === ArticleVisibility.PRIVATE ? '🔒 Privado' : '🌐 Público'}</span>
                  {!selected.published && <span className="badge badge-waiting_client">Rascunho</span>}
                  {selected.categoryId && <span className="badge badge-normal">🏷 {categoryPath(categories, selected.categoryId) ?? 'Categoria'}</span>}
                </div>
                {isStaff && (
                  <div className="flex gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={() => startEdit(selected)}>✏️ Editar</button>
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => remove(selected)}>🗑</button>
                  </div>
                )}
              </div>
              <h2>{selected.title}</h2>
              {selected.tags && selected.tags.length > 0 && (
                <div className="flex gap-1 mb-2" style={{ flexWrap: 'wrap' }}>
                  {selected.tags.map((t) => <span key={t} className="badge badge-open" style={{ fontSize: 11 }}>#{t}</span>)}
                </div>
              )}
              <div className="kb-body rich-body" style={{ lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: selected.body }} />
            </article>
          ) : (
            <div className="empty"><div className="empty-icon">👈</div>Selecione um artigo para ler{isStaff ? ' ou crie um novo' : ''}.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Formulário de criação/edição de artigo com categoria e tags.
function ArticleForm({
  draft, categoryOptions, onCancel, onSave,
}: {
  draft: Draft;
  categoryOptions: { id: string; label: string }[];
  onCancel: () => void;
  onSave: (d: Draft) => Promise<void>;
}) {
  const toast = useToast();
  const [form, setForm] = useState<Draft>(draft);
  const [tagInput, setTagInput] = useState((draft.tags ?? []).join(', '));
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Informe o título.');
    setSaving(true);
    const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      await onSave({ ...form, tags });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h2>{form.id ? 'Editar artigo' : 'Novo artigo'}</h2>
      <div className="field">
        <label className="label">Título</label>
        <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
      </div>
      <div className="field">
        <label className="label">Conteúdo</label>
        <MarkdownEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} placeholder="Escreva o artigo. Use a barra para formatar ou digite Markdown (## título, **negrito**, - lista, `código`)." minHeight={280} />
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--s3)' }}>
        <div className="field mb-0">
          <label className="label">Categoria</label>
          <select className="select input" value={form.categoryId ?? ''} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
            <option value="">— Sem categoria —</option>
            {categoryOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <div className="field mb-0">
          <label className="label">Visibilidade</label>
          <select className="select input" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value as ArticleVisibility })}>
            <option value={ArticleVisibility.PUBLIC}>🌐 Pública</option>
            <option value={ArticleVisibility.PRIVATE}>🔒 Privada</option>
          </select>
        </div>
      </div>
      <div className="field mt-3">
        <label className="label">Tags (separadas por vírgula)</label>
        <input className="input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="rede, impressora, e-mail" />
      </div>
      <label className="flex items-center gap-2 mt-2">
        <input type="checkbox" checked={form.published ?? false} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
        Publicado
      </label>
      <div className="flex gap-2 mt-4">
        <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar artigo'}</button>
        <button className="btn btn-ghost" type="button" onClick={onCancel} disabled={saving}>Cancelar</button>
      </div>
    </form>
  );
}
